'use client';

import type { CSSProperties } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowDown, ArrowUpRight, Play, RefreshCw, Waves, X } from 'lucide-react';
import PlatformEngagement from '../../../platform-engagement';
import BangdreamLive2D from '../../../bangdream-live2d';
import CharacterCardModal from './character-card-modal';
import { rewriteCatalogCdnUrl } from '../../../../lib/config/cdn';
import '../../../../app/v2/v2.css';
import './sakiko-v2.css';

export type Entry = Record<string, unknown>;

function plain(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

function imageFrom(entry: Entry): string {
  const assets = entry.assets && typeof entry.assets === 'object' ? entry.assets as Entry : {};
  return plain(entry.image) || plain(entry.iconUrl) || plain(assets.illustration) || plain(assets.icon);
}

function descriptionFrom(entry: Entry): string {
  return plain(entry.description) || plain(entry.summary) || plain(entry.cardIntro) || plain(entry.intro);
}

function displayValue(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(displayValue).filter(Boolean).join('、');
  return '';
}

function coverPaletteFromThemeColor(hex: string) {
  const channels = [1, 3, 5].map((index) => parseInt(hex.slice(index, index + 2), 16) / 255);
  const max = Math.max(...channels);
  const min = Math.min(...channels);
  const delta = max - min;
  const lightness = (max + min) / 2;
  const saturation = delta ? delta / (1 - Math.abs(2 * lightness - 1)) : 0;
  const hue = !delta ? 0 : max === channels[0]
    ? ((channels[1] - channels[2]) / delta + (channels[1] < channels[2] ? 6 : 0)) * 60
    : max === channels[1]
      ? ((channels[2] - channels[0]) / delta + 2) * 60
      : ((channels[0] - channels[1]) / delta + 4) * 60;
  const balancedSaturation = Math.max(20, Math.min(58, saturation * 100));
  const color = (amount: number, brightness: number) => `hsl(${Math.round(hue)} ${Math.round(amount)}% ${brightness}%)`;
  return {
    base: color(balancedSaturation * .48, 8),
    end: color(balancedSaturation * .72, 19),
    glow: color(Math.min(64, balancedSaturation * 1.08), 30),
    accent: color(Math.min(72, balancedSaturation * 1.08), 68),
  };
}

export type CatalogDetailProps = {
  game: string;
  catalogName: string;
  summary: Entry;
  detail: Entry;
};

const catalogKey = 'catalog_8d4fbe12ff61416bbf1d8691fe899fa3';

function rewardsText(value: unknown): string {
  if (!Array.isArray(value) || !value.length) return '—';
  return value.map((item) => {
    const reward = item && typeof item === 'object' ? item as Entry : {};
    const count = Number(reward.count);
    return `${plain(reward.name)}${Number.isFinite(count) && count !== 1 ? ` ×${count.toLocaleString()}` : ''}`;
  }).filter(Boolean).join('、') || '—';
}

function RankRows({ ranks }: { ranks: Entry[] }) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const index = Math.min(selectedIndex, ranks.length - 1);
  const rank = ranks[index];
  if (!rank) return null;
  const progress = ranks.length > 1 ? index / (ranks.length - 1) * 100 : 0;
  return <div className="bangdream-rank-slider">
    <div className="bangdream-rank-overview">
      <div className="bangdream-rank-control">
        <div className="bangdream-rank-slider-heading"><span>角色等级</span><strong>Lv.{plain(rank.rank)}</strong></div>
        <input type="range" min={0} max={ranks.length - 1} value={index} onChange={(event) => setSelectedIndex(Number(event.target.value))}
          aria-label="选择角色等级" aria-valuetext={`等级 ${plain(rank.rank)}`}
          style={{ background: `linear-gradient(to right, #4a9bb9 ${progress}%, #dce7eb ${progress}%)` }} />
        <div className="bangdream-rank-slider-ends"><span>Lv.{plain(ranks[0].rank)}</span><span>Lv.{plain(ranks[ranks.length - 1].rank)}</span></div>
      </div>
      <div className="bangdream-rank-reward"><span>所需经验值<strong>{Number(rank.exp || 0).toLocaleString()}</strong></span><span>等级奖励<strong>{rewardsText(rank.rewards)}</strong></span></div>
    </div>
    <button type="button" className="bangdream-song-toggle" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
      {showAll ? '收起全部等级奖励' : '查看全部等级奖励'}<span aria-hidden="true">{showAll ? '↑' : '↓'}</span>
    </button>
    {showAll && <div className="bangdream-rank-all">{ranks.map((item) =>
      <div key={plain(item.rank)}><strong>Lv.{plain(item.rank)}</strong><span>经验值 {Number(item.exp || 0).toLocaleString()}</span><span>{rewardsText(item.rewards)}</span></div>)}</div>}
  </div>;
}

export default function IndependentPage1({ game, catalogName, summary, detail: sourceDetail }: CatalogDetailProps) {
  const coverRef = useRef<HTMLElement>(null);
  const [coverVisible, setCoverVisible] = useState(true);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [catalogItems, setCatalogItems] = useState<Entry[]>([]);
  const [selectorBand, setSelectorBand] = useState('');
  const [selectorCharacter, setSelectorCharacter] = useState('');
  const [selectorQuery, setSelectorQuery] = useState('');
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
  const [voiceErrorId, setVoiceErrorId] = useState<string | null>(null);
  const [showAllVoices, setShowAllVoices] = useState(false);
  const [showAllSongs, setShowAllSongs] = useState(false);
  const [showAllMemberCards, setShowAllMemberCards] = useState(false);
  const [showAllSupportCards, setShowAllSupportCards] = useState(false);
  const [liveMemberCards, setLiveMemberCards] = useState<Entry[] | null>(null);
  const [liveSupportCards, setLiveSupportCards] = useState<Entry[] | null>(null);
  const openCardModal = useRef<((kind: 'member' | 'support', index: number) => void) | null>(null);
  const registerCardModal = useCallback((open: (kind: 'member' | 'support', index: number) => void) => { openCardModal.current = open; }, []);
  useEffect(() => {
    const cover = coverRef.current;
    if (!cover) return;
    const observer = new IntersectionObserver(([entry]) => setCoverVisible(entry.isIntersecting));
    observer.observe(cover);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!selectorOpen) return;
    let active = true;
    fetch(`/api/games/${encodeURIComponent(game)}/catalogs/${catalogKey}/generic/items`)
      .then((response) => response.json())
      .then((payload: unknown) => {
        const items = payload && typeof payload === 'object' && 'items' in payload ? payload.items : [];
        if (active) setCatalogItems(Array.isArray(items) ? items : []);
      })
      .catch(() => { if (active) setCatalogItems([summary]); });
    return () => { active = false; };
  }, [selectorOpen, game, summary]);
  const profile = sourceDetail.profile && typeof sourceDetail.profile === 'object' ? sourceDetail.profile as Entry : {};
  const art = sourceDetail.art && typeof sourceDetail.art === 'object' ? sourceDetail.art as Entry : {};
  const base = plain(sourceDetail.sourceBaseUrl);
  const artPath = art.character_casual && typeof art.character_casual === 'object' ? plain((art.character_casual as Entry).site_path) : '';
  const themeColor = /^#[0-9a-fA-F]{6}$/.test(plain(sourceDetail.main_color)) ? plain(sourceDetail.main_color) : '#7799cc';
  const coverPalette = coverPaletteFromThemeColor(themeColor);
  const rawName = plain(sourceDetail.name) || plain(summary.name);
  const detail: Entry = {
    name: rawName.includes('/') ? rawName.split('/').slice(1).join('/').trim() : rawName,
    subtitle: plain(profile.catch_copy),
    image: artPath ? new URL(artPath, base).toString() : plain(summary.image),
    description: plain(profile.description),
    乐队: plain(summary.type),
    担当: plain(sourceDetail.band_part),
    生日: sourceDetail.birthday_month && sourceDetail.birthday_day ? `${sourceDetail.birthday_month} 月 ${sourceDetail.birthday_day} 日` : '',
    声优: plain(profile.voice_actor),
    学校: plain(profile.school),
    班级: plain(profile.school_class),
    身高: plain(profile.height),
    星座: plain(profile.constellation),
    喜欢的食物: plain(profile.favorite_food),
    爱好: plain(profile.hobby).replace(/\s*[\r\n]+\s*/g, '、'),
  };
  const name = plain(detail.name) || plain(detail.title) || plain(summary.name) || '未命名条目';
  const image = imageFrom(detail) || imageFrom(summary);
  const description = descriptionFrom(detail) || descriptionFrom(summary);
  const excluded = new Set(['id', 'sourceId', 'name', 'title', 'subtitle', 'image', 'iconUrl', 'description', 'summary', 'cardIntro', 'intro', 'assets', '_source']);
  const fields = Object.entries(detail).filter(([key, value]) => !excluded.has(key) && displayValue(value).trim());
  const sourceMemberCards = Array.isArray(sourceDetail.memberCards) ? sourceDetail.memberCards as Entry[] : [];
  const sourceSupportCards = Array.isArray(sourceDetail.supportCards) ? sourceDetail.supportCards as Entry[] : [];
  const memberCards = liveMemberCards ?? sourceMemberCards;
  const supportCards = liveSupportCards ?? sourceSupportCards;
  useEffect(() => {
    let active = true;
    setLiveMemberCards(null);
    setLiveSupportCards(null);
    const load = async (kind: 'member' | 'support', catalog: string, sourceCards: Entry[]) => {
      const response = await fetch(`/api/games/${encodeURIComponent(game)}/catalogs/${catalog}/generic/items`);
      if (!response.ok) return;
      const payload = await response.json() as { items?: Entry[] };
      const bySourceId = new Map((payload.items || []).map((item) => [plain(item.sourceId), item]));
      const cards = sourceCards.map((source) => {
        const item = bySourceId.get(plain(source.id));
        return item ? { ...source, ...item, sourceId: plain(item.sourceId), catalogId: plain(item.id),
          title: plain(source.title) || plain(item.name), rarity: plain(source.rarity) || plain(item.grade),
        } : source;
      });
      if (active) (kind === 'member' ? setLiveMemberCards : setLiveSupportCards)(cards);
    };
    void load('member', 'catalog_675c26b105284ffe87eb36f6471f8dfc', sourceMemberCards).catch(() => {});
    void load('support', 'catalog_7d76498e4b094e88bbe587f2a3d465ab', sourceSupportCards).catch(() => {});
    return () => { active = false; };
  }, [game, sourceDetail]);
  const voices = Array.isArray(sourceDetail.voices) ? sourceDetail.voices as Entry[] : [];
  async function toggleVoice(voiceId: string, url: string) {
    const audio = audioRef.current;
    if (!audio) return;
    if (playingVoiceId === voiceId) {
      audio.pause();
      setPlayingVoiceId(null);
      return;
    }
    setVoiceErrorId(null);
    audio.pause();
    audio.src = url;
    audio.currentTime = 0;
    try {
      await audio.play();
      setPlayingVoiceId(voiceId);
    } catch {
      setPlayingVoiceId(null);
      setVoiceErrorId(voiceId);
    }
  }
  const progression = sourceDetail.progression && typeof sourceDetail.progression === 'object' ? sourceDetail.progression as Entry : {};
  const ranks = Array.isArray(progression.ranks) ? progression.ranks as Entry[] : [];
  const songs = Array.isArray(sourceDetail.songs) ? sourceDetail.songs as Entry[] : [];

  function cardSection(title: string, cards: Entry[], kind: 'member' | 'support') {
    if (!cards.length) return null;
    const showAll = kind === 'member' ? showAllMemberCards : showAllSupportCards;
    const setShowAll = kind === 'member' ? setShowAllMemberCards : setShowAllSupportCards;
    return <section className="bangdream-section">
      <header className="v2-section-head"><div><p>RELATED CARDS</p><h2>{title}</h2></div><span>{cards.length} 张</span></header>
      <div className={`bangdream-cards${kind === 'support' ? ' bangdream-support-cards' : ''}`}>{(showAll ? cards : cards.slice(0, 6)).map((card, cardIndex) => {
        const art = card.art && typeof card.art === 'object' ? card.art as Entry : {};
        const rawImage = art[kind === 'member' ? 'member_thumbnail' : 'snap_thumbnail'] || art.snap_full || art.member_full;
        const path = rawImage && typeof rawImage === 'object' ? plain((rawImage as Entry).site_path) : plain(rawImage);
        const cardImage = plain(card.image) || (path ? new URL(path, base).toString() : '');
        const maxBuild = card.max_build && typeof card.max_build === 'object' ? card.max_build as Entry : {};
        const stats = maxBuild.stats && typeof maxBuild.stats === 'object' ? maxBuild.stats as Entry : {};
        return <button type="button" className="bangdream-card" key={plain(card.id)} onClick={() => openCardModal.current?.(kind, cardIndex)} aria-label={`查看${plain(card.title)}卡牌详情`}>
          {cardImage && <img src={cardImage} alt={plain(card.title)} loading="lazy" />}
          <div><h3>{plain(card.title)}</h3><p>{plain(card.rarity)}{plain(card.display_name) ? ` · ${plain(card.display_name)}` : ''}</p>
            {Boolean(stats.total) && <small>满级综合能力 {Number(stats.total).toLocaleString()}</small>}</div>
        </button>;
      })}</div>
      {cards.length > 6 && <button type="button" className="bangdream-song-toggle" aria-expanded={showAll} onClick={() => setShowAll(!showAll)}>
        {showAll ? `收起${title}` : `查看全部${title}（${cards.length} 张）`}<span aria-hidden="true">{showAll ? '↑' : '↓'}</span>
      </button>}
    </section>;
  }

  const selectorBands = [...new Set(catalogItems.map((item) => plain(item.type)).filter(Boolean))];
  const selectorCharacters = [...new Set(catalogItems
    .filter((item) => !selectorBand || plain(item.type) === selectorBand)
    .flatMap((item) => Array.isArray(item.characters) ? item.characters.map(plain) : [plain(item.name)])
    .filter(Boolean))];
  const visibleCatalogItems = catalogItems.filter((item) =>
    (!selectorBand || plain(item.type) === selectorBand) &&
    (!selectorCharacter || (Array.isArray(item.characters) ? item.characters.map(plain) : [plain(item.name)]).includes(selectorCharacter)) &&
    (!selectorQuery.trim() || plain(item.name).toLocaleLowerCase().includes(selectorQuery.trim().toLocaleLowerCase()))
  );
  return <main className="v2-shell generic-detail-shell bangdream-detail-shell">
    <header className="v2-nav">
      <a href={`/${game}`} className="v2-brand"><Waves /><span>BanG Dream!<br /><small>CHARACTER ARCHIVE</small></span></a>
      <button className="v2-hero-switch" aria-label="切换角色" title="切换角色" aria-haspopup="dialog" aria-expanded={selectorOpen} onClick={() => { setSelectorBand(''); setSelectorCharacter(''); setSelectorQuery(''); setSelectorOpen(true); }}>
        <img src={rewriteCatalogCdnUrl(plain(summary.iconUrl) || plain((art.character_round_icon as Entry)?.site_path))} alt={`${name}头像`} /><span><RefreshCw /></span>
      </button>
    </header>
    {selectorOpen && <div className="v2-hero-selector-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setSelectorOpen(false); }}>
      <section className="v2-hero-selector" role="dialog" aria-modal="true" aria-labelledby="bangdream-selector-title">
        <header><div><p>HERO DIRECTORY</p><h2 id="bangdream-selector-title">选择角色</h2></div><button onClick={() => setSelectorOpen(false)} aria-label="关闭角色选择"><X /></button></header>
        <div className="bangdream-selector-controls">
          <select aria-label="筛选乐队" value={selectorBand} onChange={(event) => { setSelectorBand(event.target.value); setSelectorCharacter(''); }}>
            <option value="">全部乐队</option>
            {selectorBands.map((band) => <option key={band} value={band}>{band}</option>)}
          </select>
          <select aria-label="筛选角色" value={selectorCharacter} onChange={(event) => setSelectorCharacter(event.target.value)}>
            <option value="">全部角色</option>
            {selectorCharacters.map((character) => <option key={character} value={character}>{character}</option>)}
          </select>
          <input type="search" aria-label="搜索角色" placeholder="搜索角色名称" value={selectorQuery} onChange={(event) => setSelectorQuery(event.target.value)} />
        </div>
        <div className="v2-hero-selector-grid">{visibleCatalogItems.map((item) => <a key={plain(item.id)} href={`/${game}/b${plain(item.id)}`}>
          {plain(item.iconUrl) && <img src={rewriteCatalogCdnUrl(plain(item.iconUrl))} alt="" />}<span><b>{plain(item.name)}</b><small>{plain(item.type)}</small></span>
        </a>)}</div>
        {catalogItems.length > 0 && visibleCatalogItems.length === 0 && <p className="bangdream-selector-empty">没有找到匹配的角色</p>}
      </section>
    </div>}
    <section ref={coverRef} className="v2-hero generic-detail-hero" style={{
      '--character-color': themeColor,
      '--cover-auto-base': coverPalette.base,
      '--cover-auto-end': coverPalette.end,
      '--cover-auto-glow': coverPalette.glow,
      '--cover-auto-accent': coverPalette.accent,
    } as CSSProperties}>
      <div className="v2-grid" aria-hidden="true" />
      <article className="v2-copy">
        <div id="bangdream-costume-mobile-anchor" className="bangdream-costume-mobile-anchor" />
        <h1>{name}</h1>
        {plain(detail.subtitle) && <p className="v2-latin bangdream-catch-copy">{plain(detail.subtitle)}</p>}
        <div className="v2-identity generic-detail-identity">
          {fields.slice(0, 3).map(([key, value]) => <span key={key}><small>{key}</small><b>{displayValue(value)}</b></span>)}
        </div>
        <div className="v2-actions">
          <a href="#generic-detail-fields"><span><b>资料详情</b></span><ArrowDown /></a>
          <a href={`/${game}/dcatalog`}><span><b>返回图鉴页</b></span><ArrowUpRight /></a>
        </div>
      </article>
      <article className="v2-art generic-detail-art">
        <div className="v2-aura" />
        {image ? <BangdreamLive2D key={plain(summary.id)} characterId={Number(summary.sourceId || sourceDetail.sourceId || sourceDetail.id)} fallback={image} name={name} playing={coverVisible} /> : <div className="generic-detail-art-empty">{name}</div>}
      </article>
    </section>
    <section className="v2-paper generic-detail-paper">
      <div className="v2-content">
        <header className="v2-section-head" id="generic-detail-fields"><div><p>REFERENCE DATA</p><h2>资料详情</h2></div><span>{catalogName}</span></header>
        {description && <p className="bangdream-profile-description">{description}</p>}
        {fields.length > 0 ? <dl className="generic-detail-fields">{fields.map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{displayValue(value)}</dd></div>)}</dl> : <p className="generic-detail-empty">暂无更多资料</p>}
        {ranks.length > 0 && <section className="bangdream-section" id="character-progression">
          <header className="v2-section-head"><div><p>CHARACTER PROGRESSION</p><h2>角色等级奖励</h2></div><span>{ranks.length} 级</span></header>
          <RankRows ranks={ranks} />
        </section>}
        {cardSection('成员卡', memberCards, 'member')}
        {cardSection('关联支援卡', supportCards, 'support')}
        {voices.length > 0 && <section className="bangdream-section"><header className="v2-section-head"><div><p>VOICE ARCHIVE</p><h2>角色语音</h2></div><span>{voices.length} 条</span></header>
          <audio ref={audioRef} preload="none" onEnded={() => setPlayingVoiceId(null)} onError={() => { setVoiceErrorId(playingVoiceId); setPlayingVoiceId(null); }} />
          <div className="bangdream-voices">{(showAllVoices ? voices : voices.slice(0, 6)).map((voice, index) => {
            const voiceId = plain(voice.id) || String(index);
            const audio = voice.audio && typeof voice.audio === 'object' ? voice.audio as Entry : {};
            const path = plain(audio.site_path);
            const url = path ? rewriteCatalogCdnUrl(new URL(path.startsWith('//') ? `https:${path}` : path, base).toString()) : '';
            return <article key={voiceId} className={playingVoiceId === voiceId ? 'bangdream-voice-active' : undefined}>
              <strong>{plain(voice.title) || plain(voice.name) || `语音 ${index + 1}`}</strong>
              {url && <button type="button" className="bangdream-voice-play" aria-label={`${playingVoiceId === voiceId ? '暂停' : '播放'}语音 ${index + 1}`} aria-pressed={playingVoiceId === voiceId} onClick={() => void toggleVoice(voiceId, url)}>
                {playingVoiceId === voiceId ? <span className="bangdream-voice-wave" aria-hidden="true"><i /><i /><i /><i /></span> : <Play />}
              </button>}
              <p>{plain(voice.text) || plain(voice.transcript) || plain(voice.description) || '暂无台词文本'}</p>
              {voiceErrorId === voiceId && <small className="bangdream-voice-error">音频加载失败</small>}
            </article>;
          })}</div>
          {voices.length > 6 && <button type="button" className="bangdream-song-toggle" aria-expanded={showAllVoices} onClick={() => setShowAllVoices(!showAllVoices)}>
            {showAllVoices ? '收起语音' : `查看全部语音（${voices.length} 句）`}<span aria-hidden="true">{showAllVoices ? '↑' : '↓'}</span>
          </button>}
        </section>}
        {songs.length > 0 && <section className="bangdream-section" id="band-songs">
          <header className="v2-section-head"><div><p>MUSIC & CHARTS</p><h2>相关歌曲</h2></div><span>Ave Mujica · {songs.length} 首</span></header>
          <div className="bangdream-song-list">{(showAllSongs ? songs : songs.slice(0, 3)).map((song) => {
            const jacket = song.jacket && typeof song.jacket === 'object' ? song.jacket as Entry : {};
            const jacketPath = plain(jacket.site_path || jacket.thumbnail_site_path);
          const jacketUrl = jacketPath ? rewriteCatalogCdnUrl(new URL(jacketPath, base).toString()) : '';
            const difficulties = Array.isArray(song.difficulties) ? song.difficulties as Entry[] : [];
            return <article className="bangdream-song" key={plain(song.id)}>
              {jacketUrl && <img src={jacketUrl} alt={`${plain(song.title)}封面`} loading="lazy" />}
              <div className="bangdream-song-title"><h3>{plain(song.title)}</h3><small>Ave Mujica</small></div>
              <p className="bangdream-song-credits"><span>作词：{plain(song.lyricist) || '—'}</span><span>作曲：{plain(song.composer) || '—'}</span><span>编曲：{plain(song.arranger) || '—'}</span></p>
              <div className="bangdream-difficulties">{difficulties.map((difficulty) =>
                <span key={plain(difficulty.score_id)}><b>{plain(difficulty.name).toUpperCase()} {plain(difficulty.display_level)}</b><small>{Number(difficulty.full_combo_notes || 0).toLocaleString()} 音符</small></span>)}</div>
            </article>;
          })}</div>
          {songs.length > 3 && <button type="button" className="bangdream-song-toggle" aria-expanded={showAllSongs} onClick={() => setShowAllSongs(!showAllSongs)}>
            {showAllSongs ? '收起歌曲' : `查看全部歌曲（${songs.length} 首）`}<span aria-hidden="true">{showAllSongs ? '↑' : '↓'}</span>
          </button>}
        </section>}
      </div>
    </section>
    <PlatformEngagement linkUrl={`/${game}/b${plain(summary.id)}`} subject="角色" title={name} maxWidth={1080} visible={!coverVisible} />
    <CharacterCardModal game={game} base={base} characterName={name} memberCards={memberCards} supportCards={supportCards} onRegister={registerCardModal} />
  </main>;
}
